import { createClient } from '@supabase/supabase-js';

// Support both Vite (import.meta.env) and Node.js (process.env) environments
// This allows Playwright tests to import this module without errors
const getEnvVar = (name: string): string | undefined => {
  // In Vite/browser context
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    return import.meta.env[name];
  }
  // In Node.js/test context
  if (typeof process !== 'undefined' && process.env) {
    return process.env[name];
  }
  return undefined;
};

export let supabaseUrl = getEnvVar('VITE_SUPABASE_URL');
export let supabaseAnonKey = getEnvVar('VITE_SUPABASE_ANON_KEY');

// Detect if we're in a test environment.
// Vitest runs unit tests in JSDOM (window exists), but still needs safe defaults.
const isTestEnvironment =
  // Vite/Vitest convention
  (typeof import.meta !== 'undefined' &&
    !!import.meta.env &&
    (import.meta.env.MODE === 'test' || (import.meta.env as any).VITEST === 'true')) ||
  // Node convention
  (typeof process !== 'undefined' &&
    !!process.env &&
    (process.env.NODE_ENV === 'test' || process.env.VITEST === 'true'));

// Detect if we're in a Node.js environment (not browser)
const isNodeEnvironment = typeof process !== 'undefined' && typeof window === 'undefined';

// In test/Node.js environment, use dummy values if env vars are missing
// This allows Playwright tests to import this module without errors
if (!supabaseUrl || !supabaseAnonKey) {
  if (isNodeEnvironment || isTestEnvironment) {
    supabaseUrl = 'https://test.supabase.co';
    supabaseAnonKey = 'test-anon-key';
  } else {
    throw new Error('Missing Supabase environment variables');
  }
}

// Type assertion: At this point, both values are guaranteed to be strings
// Either from env vars or from test fallback values
if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Supabase configuration error: URL and key must be defined');
}

const secureStorage = {
  getItem: (key: string): string | null => {
    try {
      if (typeof window === 'undefined') return null;
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  setItem: (key: string, value: string): void => {
    try {
      if (typeof window === 'undefined') return;
      // Store the full payload including refresh_token. Stripping is handled
      // explicitly by stripSupabaseRefreshTokenFromStorage() after auth flows
      // (sign-in, MFA-complete) have had a chance to forward the token to the
      // server for HttpOnly cookie creation.
      window.localStorage.setItem(key, value);
    } catch {
      // Ignore storage write failures (private mode/quota issues)
    }
  },
  removeItem: (key: string): void => {
    try {
      if (typeof window === 'undefined') return;
      window.localStorage.removeItem(key);
    } catch {
      // Ignore storage removal failures
    }
  },
};

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    storage: secureStorage as any,
    persistSession: true,
    autoRefreshToken: false,
  },
});
